import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react"
import { useStore } from "zustand"

import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"
import type { GuildWarRosterBoard } from "@/lib/guild-war-api"

import { Button } from "@/components/button"
import { RosterExportCard } from "@/components/guild-war/roster-export-card"
import { TeamBoardExportPreviewDialog } from "@/components/guild-war/team-board-export-preview-dialog"
import {
  type RosterPageUiStore,
  useRosterIsExporting,
  useRosterShowExportPreview,
} from "@/lib/stores/roster-page-ui-store"

const FOCUSABLE_SELECTOR =
  'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })

const EXPORT_ERROR = "Failed to export the roster image. Please try again."

export function RosterExportManager({
  store,
  board,
  candidateById,
  displayDateLabel,
}: {
  store: RosterPageUiStore
  board: GuildWarRosterBoard
  candidateById: Map<string, GuildWarCandidatePoolMember>
  displayDateLabel: string
}) {
  const exportRef = useRef<HTMLDivElement | null>(null)
  const exportPreviewDialogRef = useRef<HTMLDivElement | null>(null)
  const exportAttemptRef = useRef(0)
  const [exportError, setExportError] = useState<string | null>(null)
  const showExportPreview = useRosterShowExportPreview(store)
  const isExporting = useRosterIsExporting(store)
  const setShowExportPreview = useStore(store, (s) => s.setShowExportPreview)
  const setIsExporting = useStore(store, (s) => s.setIsExporting)

  useEffect(() => {
    if (!showExportPreview) {
      return
    }

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    const frame = requestAnimationFrame(() => {
      const firstFocusable =
        exportPreviewDialogRef.current?.querySelector<HTMLElement>(
          FOCUSABLE_SELECTOR,
        )

      firstFocusable?.focus()
    })

    return () => {
      cancelAnimationFrame(frame)
      previouslyFocused?.focus()
    }
  }, [showExportPreview])

  const handleDownloadExport = async () => {
    const normalizedDate = displayDateLabel.trim() || "guild-war"
    const attempt = ++exportAttemptRef.current
    setExportError(null)
    setIsExporting(true)

    try {
      await waitForNextPaint()

      if (!exportRef.current) {
        throw new Error("Missing export element")
      }

      const { toPng } = await import("html-to-image")
      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#33454f",
        skipFonts: true,
      })

      if (exportAttemptRef.current !== attempt) {
        return
      }

      const anchor = document.createElement("a")
      anchor.download = `guild-war-roster-${normalizedDate.replaceAll(".", "-")}.png`
      anchor.href = dataUrl
      anchor.click()
    } catch {
      if (exportAttemptRef.current === attempt) {
        setExportError(EXPORT_ERROR)
      }
    } finally {
      if (exportAttemptRef.current === attempt) {
        setIsExporting(false)
      }
    }
  }

  const handleClose = () => {
    exportAttemptRef.current += 1
    setExportError(null)
    setIsExporting(false)
    setShowExportPreview(false)
  }

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault()
      handleClose()
      return
    }

    if (event.key !== "Tab") {
      return
    }

    const dialog = exportPreviewDialogRef.current
    if (!dialog) {
      return
    }

    const focusable = Array.from(
      dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((element) => !element.hasAttribute("disabled"))

    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    const active = document.activeElement

    if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
      return
    }

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    }
  }

  return (
    <>
      <TeamBoardExportPreviewDialog
        open={showExportPreview}
        isExporting={isExporting}
        exportError={exportError}
        board={board}
        candidateById={candidateById}
        displayDateLabel={displayDateLabel}
        dialogRef={exportPreviewDialogRef}
        onClose={handleClose}
        onDownload={() => {
          void handleDownloadExport()
        }}
        onDialogKeyDown={handleDialogKeyDown}
      />

      {isExporting ? (
        <div className="pointer-events-none fixed top-0 -left-2500">
          <div ref={exportRef}>
            <RosterExportCard
              displayDateLabel={displayDateLabel}
              board={board}
              candidateById={candidateById}
            />
          </div>
        </div>
      ) : null}
    </>
  )
}
